import React, { useState } from 'react';
import { Calendar, Wrench, CheckCircle, Clock, AlertTriangle, Plus, Settings, X, Search, Filter, ChevronDown, Edit2, Trash2, MoreVertical } from 'lucide-react';

interface MaintenanceTask {
  id: number;
  title: string;
  asset: string;
  date: string;
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
  priority: 'high' | 'medium' | 'low';
  assignee: string;
  description?: string;
  notes?: string[];
  checklist?: { task: string; completed: boolean }[];
}

export const Maintenance: React.FC = () => {
  const [tasks, setTasks] = useState<MaintenanceTask[]>([
    {
      id: 1,
      title: 'RO Membrane Cleaning',
      asset: 'RO Train 1',
      date: '2024-03-20',
      status: 'scheduled',
      priority: 'high',
      assignee: 'John Smith',
      description: 'Perform chemical cleaning of RO membranes to remove scaling and fouling',
      notes: ['Last cleaning performed 3 months ago', 'Check differential pressure before cleaning'],
      checklist: [
        { task: 'Prepare cleaning solution', completed: false },
        { task: 'Check all valves', completed: false },
        { task: 'Monitor pH levels', completed: false },
        { task: 'Record performance data', completed: false }
      ]
    },
    {
      id: 2,
      title: 'Filter Cartridge Replacement',
      asset: 'Pre-treatment System',
      date: '2024-03-22',
      status: 'in-progress',
      priority: 'medium',
      assignee: 'Sarah Johnson',
      description: 'Replace cartridge filters in pre-treatment system',
      notes: ['Pressure differential exceeds threshold', 'Order replacement cartridges'],
      checklist: [
        { task: 'Isolate filter housing', completed: true },
        { task: 'Drain system', completed: true },
        { task: 'Replace cartridges', completed: false },
        { task: 'Check for leaks', completed: false }
      ]
    },
    {
      id: 3,
      title: 'Pump Maintenance',
      asset: 'High Pressure Pump',
      date: '2024-03-25',
      status: 'completed',
      priority: 'low',
      assignee: 'Mike Brown',
      description: 'Routine maintenance of high-pressure pump',
      notes: ['Vibration levels normal', 'Replaced mechanical seal'],
      checklist: [
        { task: 'Check alignment', completed: true },
        { task: 'Lubricate bearings', completed: true },
        { task: 'Inspect seals', completed: true },
        { task: 'Test operation', completed: true }
      ]
    }
  ]);

  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [showCalendarView, setShowCalendarView] = useState(false);
  const [showTaskDetails, setShowTaskDetails] = useState<number | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [editingTask, setEditingTask] = useState<MaintenanceTask | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [newTask, setNewTask] = useState<Partial<MaintenanceTask>>({
    status: 'scheduled',
    priority: 'medium',
    checklist: []
  });

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-600 bg-red-50';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50';
      case 'low':
        return 'text-green-600 bg-green-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <Clock className="h-5 w-5 text-blue-500" />;
      case 'in-progress':
        return <Settings className="h-5 w-5 text-yellow-500" />;
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'cancelled':
        return <X className="h-5 w-5 text-red-500" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-gray-500" />;
    }
  };

  const handleStatusChange = (taskId: number, newStatus: MaintenanceTask['status']) => {
    setTasks(prevTasks =>
      prevTasks.map(task =>
        task.id === taskId
          ? { ...task, status: newStatus }
          : task
      )
    );
  };

  const handleChecklistToggle = (taskId: number, checklistIndex: number) => {
    setTasks(prevTasks =>
      prevTasks.map(task =>
        task.id === taskId && task.checklist
          ? {
              ...task,
              checklist: task.checklist.map((item, index) =>
                index === checklistIndex
                  ? { ...item, completed: !item.completed }
                  : item
              )
            }
          : task
      )
    );
  };

  const handleAddNote = (taskId: number, note: string) => {
    setTasks(prevTasks =>
      prevTasks.map(task =>
        task.id === taskId
          ? { ...task, notes: [...(task.notes || []), note] }
          : task
      )
    );
  };

  const handleDeleteTask = (taskId: number) => {
    setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
    setShowDeleteConfirm(null);
  };

  const handleSaveNewTask = () => {
    if (newTask.title && newTask.asset && newTask.date && newTask.assignee) {
      setTasks(prevTasks => [
        ...prevTasks,
        {
          ...newTask as MaintenanceTask,
          id: Math.max(...prevTasks.map(t => t.id)) + 1
        }
      ]);
      setShowNewTaskModal(false);
      setNewTask({
        status: 'scheduled',
        priority: 'medium',
        checklist: []
      });
    }
  };

  const handleUpdateTask = () => {
    if (editingTask) {
      setTasks(prevTasks =>
        prevTasks.map(task =>
          task.id === editingTask.id
            ? editingTask
            : task
        )
      );
      setEditingTask(null);
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         task.asset.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         task.assignee.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = selectedStatus.length === 0 || selectedStatus.includes(task.status);
    const matchesPriority = selectedPriorities.length === 0 || selectedPriorities.includes(task.priority);
    return matchesSearch && matchesStatus && matchesPriority;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Maintenance Schedule</h2>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
            
            <button
              onClick={() => setShowCalendarView(!showCalendarView)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
            >
              <Calendar className="h-4 w-4" />
              <span>View Calendar</span>
            </button>

            <div className="relative">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
              >
                <Filter className="h-4 w-4" />
                <span>Filters</span>
                <ChevronDown className="h-4 w-4" />
              </button>

              {showFilters && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg p-4 z-10 border border-gray-200">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                      <div className="space-y-2">
                        {['scheduled', 'in-progress', 'completed', 'cancelled'].map(status => (
                          <label key={status} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedStatus.includes(status)}
                              onChange={() => {
                                setSelectedStatus(prev =>
                                  prev.includes(status)
                                    ? prev.filter(s => s !== status)
                                    : [...prev, status]
                                );
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-600 capitalize">{status}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                      <div className="space-y-2">
                        {['high', 'medium', 'low'].map(priority => (
                          <label key={priority} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedPriorities.includes(priority)}
                              onChange={() => {
                                setSelectedPriorities(prev =>
                                  prev.includes(priority)
                                    ? prev.filter(p => p !== priority)
                                    : [...prev, priority]
                                );
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-600 capitalize">{priority}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => setShowNewTaskModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              <span>Schedule Task</span>
            </button>
          </div>
        </div>

        {/* Tasks Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTasks.map((task) => (
            <div key={task.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Wrench className="h-6 w-6 text-blue-500" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{task.title}</h3>
                    <p className="text-sm text-gray-500">{task.asset}</p>
                  </div>
                </div>
                <div className="relative">
                  <button
                    onClick={() => setShowTaskDetails(task.id)}
                    className="p-1 hover:bg-gray-100 rounded-full"
                  >
                    <MoreVertical className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Date:</span>
                  <span className="text-sm font-medium text-gray-900">{task.date}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Assignee:</span>
                  <span className="text-sm font-medium text-gray-900">{task.assignee}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Priority:</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getPriorityColor(task.priority)}`}>
                    {task.priority}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Status:</span>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(task.status)}
                    <span className="text-sm font-medium capitalize text-gray-900">{task.status}</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 space-y-2">
                {task.checklist && (
                  <div className="text-sm text-gray-600">
                    <p className="font-medium mb-2">Progress: {task.checklist.filter(item => item.completed).length}/{task.checklist.length} tasks completed</p>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{
                          width: `${(task.checklist.filter(item => item.completed).length / task.checklist.length) * 100}%`
                        }}
                      />
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setShowTaskDetails(task.id)}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <Settings className="h-4 w-4" />
                  <span>View Details</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Task Details Modal */}
      {showTaskDetails !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full m-4">
            {tasks.find(task => task.id === showTaskDetails) && (
              <>
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {tasks.find(task => task.id === showTaskDetails)?.title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {tasks.find(task => task.id === showTaskDetails)?.asset}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowTaskDetails(null)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Description</h4>
                    <p className="text-gray-600">
                      {tasks.find(task => task.id === showTaskDetails)?.description}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Status</h4>
                      <select
                        value={tasks.find(task => task.id === showTaskDetails)?.status}
                        onChange={(e) => handleStatusChange(showTaskDetails, e.target.value as MaintenanceTask['status'])}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="scheduled">Scheduled</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Priority</h4>
                      <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                        getPriorityColor(tasks.find(task => task.id === showTaskDetails)?.priority || '')
                      }`}>
                        {tasks.find(task => task.id === showTaskDetails)?.priority}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Checklist</h4>
                    <div className="space-y-2">
                      {tasks.find(task => task.id === showTaskDetails)?.checklist?.map((item, index) => (
                        <label key={index} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={item.completed}
                            onChange={() => handleChecklistToggle(showTaskDetails, index)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-600">{item.task}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Notes</h4>
                    <div className="space-y-2">
                      {tasks.find(task => task.id === showTaskDetails)?.notes?.map((note, index) => (
                        <p key={index} className="text-sm text-gray-600">{note}</p>
                      ))}
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          placeholder="Add a note..."
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && (e.target as HTMLInputElement).value.trim()) {
                              handleAddNote(showTaskDetails, (e.target as HTMLInputElement).value.trim());
                              (e.target as HTMLInputElement).value = '';
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <button
                      onClick={() => setShowDeleteConfirm(showTaskDetails)}
                      className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Delete Task</span>
                    </button>
                    <div className="space-x-4">
                      <button
                        onClick={() => setShowTaskDetails(null)}
                        className="px-4 py-2 text-gray-600 hover:text-gray-700"
                      >
                        Close
                      </button>
                      <button
                        onClick={() => {
                          setEditingTask(tasks.find(task => task.id === showTaskDetails) || null);
                          setShowTaskDetails(null);
                        }}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        <Edit2 className="h-4 w-4" />
                        <span>Edit Task</span>
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* New Task Modal */}
      {showNewTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full m-4">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-xl font-bold text-gray-900">Schedule New Task</h3>
              <button
                onClick={() => setShowNewTaskModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Task Title
                </label>
                <input
                  type="text"
                  value={newTask.title || ''}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Asset
                </label>
                <input
                  type="text"
                  value={newTask.asset || ''}
                  onChange={(e) => setNewTask({ ...newTask, asset: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date
                  </label>
                  <input
                    type="date"
                    value={newTask.date || ''}
                    onChange={(e) => setNewTask({ ...newTask, date: e.target.value })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assignee
                  </label>
                  <input
                    type="text"
                    value={newTask.assignee || ''}
                    onChange={(e) => setNewTask({ ...newTask, assignee: e.target.value })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={newTask.description || ''}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    value={newTask.priority}
                    onChange={(e) => setNewTask({ ...newTask, priority: e.target.value as MaintenanceTask['priority'] })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={newTask.status}
                    onChange={(e) => setNewTask({ ...newTask, status: e.target.value as MaintenanceTask['status'] })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="scheduled">Scheduled</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowNewTaskModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveNewTask}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save Task
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full m-4">
            <div className="text-center">
              <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Task</h3>
              <p className="text-sm text-gray-500">
                Are you sure you want to delete this task? This action cannot be undone.
              </p>
            </div>
            <div className="mt-6 flex justify-center space-x-4">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteTask(showDeleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};